import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { LineChart } from '@mui/x-charts/LineChart';
import chartColorPalette from 'assets/styles/chartColorPalette';
import type React from 'react';
import type { WeeklyData } from 'services/types/survey';
import { NAVY, RADIUS_COIN, SHADOW, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';

interface SurveysEvolutionChartProps {
  data: WeeklyData[] | undefined;
  isLoading: boolean;
}

const MONTH_NAMES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

// week is the Monday of the ISO week, "YYYY-MM-DD" -> "29 Jun"
const formatWeek = (week: string): string => {
  const [, month, day] = week.split('-');
  return `${parseInt(day, 10)} ${MONTH_NAMES[parseInt(month, 10) - 1]}`;
};

const SurveysEvolutionChart: React.FC<SurveysEvolutionChartProps> = ({ data, isLoading }) => {
  if (isLoading) {
    return (
      <Box
        sx={{
          my: 1.5,
          backgroundColor: SURFACE_CONTAINER_LOWEST,
          borderRadius: RADIUS_COIN,
          boxShadow: SHADOW,
          px: 3,
          pt: 2.5,
          pb: 2,
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Jost, sans-serif',
            fontWeight: 700,
            fontSize: '1.15rem',
            color: NAVY,
            mb: 2,
          }}
        >
          Evolución Semanal de Encuestas
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <CircularProgress sx={{ color: NAVY }} />
        </Box>
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box
        sx={{
          my: 1.5,
          backgroundColor: SURFACE_CONTAINER_LOWEST,
          borderRadius: RADIUS_COIN,
          boxShadow: SHADOW,
          px: 3,
          pt: 2.5,
          pb: 2,
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Jost, sans-serif',
            fontWeight: 700,
            fontSize: '1.15rem',
            color: NAVY,
            mb: 2,
          }}
        >
          Evolución Semanal de Encuestas
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
          <Typography sx={{ fontFamily: 'Jost, sans-serif', color: `${NAVY}80` }}>No hay datos para mostrar</Typography>
        </Box>
      </Box>
    );
  }

  const xLabels = data.map((d) => formatWeek(d.week));

  return (
    <Box
      sx={{
        my: 1.5,
        backgroundColor: SURFACE_CONTAINER_LOWEST,
        borderRadius: RADIUS_COIN,
        boxShadow: SHADOW,
        overflow: 'hidden',
        transition: 'box-shadow 0.2s ease',
        '&:hover': {
          boxShadow: '0px 16px 40px rgba(21, 24, 58, 0.1)',
        },
      }}
    >
      <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
        <Typography
          sx={{
            fontFamily: 'Jost, sans-serif',
            fontWeight: 700,
            fontSize: '1.15rem',
            color: NAVY,
          }}
        >
          Evolución Semanal de Encuestas
        </Typography>
        <Box sx={{ width: '100%', height: 350 }}>
          <LineChart
            xAxis={[{ scaleType: 'point', data: xLabels, height: 'auto' as const }]}
            colors={chartColorPalette}
            series={[
              {
                data: data.map((d) => d.loaded),
                label: 'Cargadas',
                curve: 'linear',
                showMark: true,
              },
              {
                data: data.map((d) => d.triggered),
                label: 'Enviadas',
                curve: 'linear',
                showMark: true,
              },
              {
                data: data.map((d) => d.finished),
                label: 'Respondidas',
                curve: 'linear',
                showMark: true,
              },
            ]}
            height={300}
            margin={{ left: 50, right: 20, top: 20, bottom: 30 }}
            slotProps={{
              legend: {
                direction: 'horizontal',
                position: { vertical: 'top', horizontal: 'center' },
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default SurveysEvolutionChart;
